import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { peopleApi, occasionsApi, giftsApi, affiliateApi, sharingApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import useMinLoad from "../hooks/useMinLoad";
import BackButtonTrap from "../components/shared/BackButtonTrap";
import useOnEscape from "../hooks/useOnEscape";
import useFormPresentation from "../hooks/useFormPresentation";
import { buildRetailerLinks } from "../utils/retailerLinks";

// Gift occasions (added from People page)
const OCCASION_TYPES = [
  "birthday", "christmas", "anniversary", "mother's_day",
  "father's_day", "graduation", "hanukkah", "valentine's_day",
  "wedding", "baby_shower", "just_because", "other"
];

const EVENT_OCCASION_TYPES = new Set([
  "trip",
  "concert",
  "graduation_party",
  "holiday_gathering",
  "game_night",
  "anniversary_dinner",
  "birthday_party",
  "other_event",
]);

const GIFT_EVENT_TYPES = new Set(["wedding", "graduation_party", "baby_shower", "birthday_party"]);

function GiftSheetTopRow({ onClose, handleProps = {}, closeLabel = "Close" }) {
  return (
    <div className="gf-sheet-top-row">
      <button
        type="button"
        className="gf-sheet-handle"
        {...handleProps}
        onClick={(event) => {
          handleProps.onClick?.(event);
          if (!event.defaultPrevented) onClose?.();
        }}
      />
      <button type="button" className="gf-sheet-top-close" onClick={onClose} aria-label={closeLabel}>×</button>
    </div>
  );
}

function GiftSheetHeader({ title, icon = "🎁", subtitle = "" }) {
  return (
    <>
      <div className="gf-sheet-logo-row">
        <div className="gf-sheet-brand">
          <div className="gf-sheet-logo-ico" aria-hidden="true">{icon}</div>
          <span className="gf-sheet-title">{title}</span>
        </div>
      </div>
      {subtitle && <div className="gf-sheet-subtitle">{subtitle}</div>}
    </>
  );
}

// All known occasion/event type icons (covers both People occasions and Events & trips)
const OCCASION_ICONS = {
  birthday:             "🎂",
  christmas:            "🎄",
  anniversary:          "💍",
  "mother's_day":       "💐",
  "father's_day":       "👔",
  graduation:           "🎓",
  hanukkah:             "🕎",
  "valentine's_day":    "❤️",
  wedding:              "💍",
  baby_shower:          "🍼",
  just_because:         "🎁",
  other:                "📅",
  // Event & trips types
  trip:                 "✈️",
  concert:              "🎵",
  graduation_party:     "🎓",
  holiday_gathering:    "🎄",
  game_night:           "🎲",
  anniversary_dinner:   "🕯️",
  birthday_party:       "🎂",
  other_event:          "📅",
};

const AVATAR_COLORS = [
  ["#FDEEE9", "#E7614A"], ["#EAF3EE", "#4A7C59"],
  ["#FDF6E3", "#C9A84C"], ["#EEF2FF", "#6366F1"],
  ["#FDF2F8", "#C026D3"], ["#E0F2FE", "#0369A1"],
];

function isGiftOccasionType(type) {
  return !EVENT_OCCASION_TYPES.has(type) || GIFT_EVENT_TYPES.has(type);
}

function isPersonOccasionType(type) {
  return OCCASION_TYPES.includes(type) || type === "trip";
}

function urgencyClass(days) {
  if (days === null || days === undefined) return "is-calm";
  if (days <= 14) return "is-urgent";
  if (days <= 30) return "is-soon";
  return "is-calm";
}

function isUniqueGiftIdea(item) {
  const text = `${item?.category || ""} ${item?.title || item?.gift_name || ""} ${item?.description || ""} ${item?.why_it_fits || ""}`.toLowerCase();
  return item?.category === "experience" || /(unique|personalized|custom|handmade|hard to shop)/.test(text);
}

function formatGiftHistoryDate(entry) {
  if (entry?.created_at) {
    const date = new Date(entry.created_at);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
  }
  return entry?.year ? String(entry.year) : "";
}

function occasionMonthDay(occasion) {
  const value = occasion?.occasion_date || occasion?.next_occurrence;
  return value ? String(value).slice(5, 10) : "";
}

function dedupeEventGiftOccasions(occasions) {
  const birthdayDays = new Set(
    occasions
      .filter(occasion => occasion.occasion_type === "birthday")
      .map(occasionMonthDay)
      .filter(Boolean)
  );
  const anniversaryDays = new Set(
    occasions
      .filter(occasion => occasion.occasion_type === "anniversary")
      .map(occasionMonthDay)
      .filter(Boolean)
  );

  return occasions.filter(occasion => {
    const day = occasionMonthDay(occasion);
    if (occasion.occasion_type === "birthday_party" && birthdayDays.has(day)) return false;
    if (occasion.occasion_type === "anniversary_dinner" && anniversaryDays.has(day)) return false;
    return true;
  });
}

function getLastCelebratedDate(occasion) {
  if (!occasion?.is_recurring || !occasion?.occasion_date || !occasion?.next_occurrence) return null;
  const baseDate = new Date(`${occasion.occasion_date}T00:00:00`);
  const nextDate = new Date(`${occasion.next_occurrence}T00:00:00`);
  if (Number.isNaN(baseDate.getTime()) || Number.isNaN(nextDate.getTime())) return null;
  return new Date(nextDate.getFullYear() - 1, baseDate.getMonth(), baseDate.getDate());
}

function OccasionForm({ personId, initial = null, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    occasion_type: "birthday",
    occasion_date: "",
    is_recurring: true,
    budget_override_min: "",
    budget_override_max: "",
    notes: "",
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!initial) {
      setForm({
        occasion_type: "birthday",
        occasion_date: "",
        is_recurring: true,
        budget_override_min: "",
        budget_override_max: "",
        notes: "",
      });
      return;
    }
    setForm({
      occasion_type: initial.occasion_type || "birthday",
      occasion_date: initial.occasion_date || "",
      is_recurring: initial.is_recurring ?? true,
      budget_override_min: initial.budget_override_min ?? "",
      budget_override_max: initial.budget_override_max ?? "",
      notes: initial.notes || "",
    });
  }, [initial]);

  const handleSubmit = e => {
    e.preventDefault();
    const payload = { ...form, person_id: personId };
    if (!payload.budget_override_min) delete payload.budget_override_min;
    if (!payload.budget_override_max) delete payload.budget_override_max;
    onSave(payload);
  };

  return (
    <form className="person-occasion-sheet-form" onSubmit={handleSubmit}>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Occasion type</label>
          <select className="form-select" value={form.occasion_type} onChange={set("occasion_type")}>
            {OCCASION_TYPES.map(t => {
              const icon = OCCASION_ICONS[t] || "📅";
              const label = t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
              return <option key={t} value={t}>{icon} {label}</option>;
            })}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Date *</label>
          <input type="date" className="form-input" value={form.occasion_date} onChange={set("occasion_date")} required />
          <span className="text-sm" style={{ color: "var(--ink-hint)", marginTop: 4, display: "block" }}>
            For yearly occasions, enter this year's date.
          </span>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_recurring}
            onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))}
          />
          Repeat every year
        </label>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Budget override min ($) <span style={{ fontWeight: 400, color: "var(--ink-hint)" }}>— leave blank to use person default</span></label>
          <input type="text" inputMode="numeric" className="form-input" value={form.budget_override_min} onChange={set("budget_override_min")} placeholder="e.g. 25" />
        </div>
        <div className="form-group">
          <label className="form-label">Budget override max ($) <span style={{ fontWeight: 400, color: "var(--ink-hint)" }}>— leave blank to use person default</span></label>
          <input type="text" inputMode="numeric" className="form-input" value={form.budget_override_max} onChange={set("budget_override_max")} placeholder="e.g. 100" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Notes</label>
        <input className="form-input" value={form.notes} onChange={set("notes")} placeholder="Big milestone this year..." />
      </div>

      <div className="flex-center gap-8 mt-16 person-occasion-sheet-actions" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (initial ? "Saving..." : "Adding...") : (initial ? "Save changes" : "Add occasion")}
        </button>
      </div>
    </form>
  );
}

function FormPresentation({
  title,
  onClose,
  children,
  subtitle = "Add the date, budget, and notes for this person.",
  compact = false,
}) {
  const presentation = useFormPresentation();
  const sheetRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, currentY: 0 });

  const closeSheet = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.style.transform = "";
      sheetRef.current.style.transition = "";
    }
    onClose();
  }, [onClose]);

  const startDrag = useCallback((event) => {
    dragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }, []);

  const moveDrag = useCallback((event) => {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = Math.max(0, event.clientY - dragRef.current.startY);
    dragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    sheetRef.current.style.transform = `translateY(${delta}px)`;
  }, []);

  const endDrag = useCallback((event) => {
    if (!dragRef.current.active || !sheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = dragRef.current.currentY;
    dragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > 72) {
      sheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      sheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(closeSheet, 205);
      return;
    }
    sheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    sheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!sheetRef.current || dragRef.current.active) return;
      sheetRef.current.style.transform = "";
      sheetRef.current.style.transition = "";
    }, 190);
  }, [closeSheet]);

  if (presentation === "modal") {
    return (
      <CenteredModal title={title} onClose={onClose}>
        {children}
      </CenteredModal>
    );
  }

  return (
    <div className={`hm-gifts-sheet-overlay${compact ? " hm-gifts-sheet-overlay--center" : ""}`} onClick={closeSheet}>
      <BackButtonTrap onClose={closeSheet} />
      <div
        id="person-occasion-form"
        className={`hm-gifts-action-sheet person-occasion-form-sheet${compact ? " hm-gifts-confirm-sheet" : ""}`}
        ref={sheetRef}
        onClick={event => event.stopPropagation()}
      >
        <GiftSheetTopRow
          onClose={closeSheet}
          closeLabel={`Close ${title}`}
          handleProps={{
            onPointerDown: startDrag,
            onPointerMove: moveDrag,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            "aria-label": "Drag down to close",
          }}
        />
        <GiftSheetHeader title={title} subtitle={subtitle} />
        <div className="hm-gifts-sheet-form-body">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function PersonDetail({ embeddedPersonId = null, embeddedSheet = false, onCloseEmbedded = null } = {}) {
  const { shoppingRegion, user } = useAuth();
  const { personId: routePersonId } = useParams();
  const personId = embeddedPersonId || routePersonId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [person, setPerson] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOccForm, setShowOccForm] = useState(false);
  const [savingOcc, setSavingOcc] = useState(false);
  const [editingOccasion, setEditingOccasion] = useState(null);
  const [confirmDeleteOcc, setConfirmDeleteOcc] = useState(null);
  const [confirmDeleteHistory, setConfirmDeleteHistory] = useState(null);
  const [deletingOccasionId, setDeletingOccasionId] = useState(null);
  const [deleteNoticeMessage, setDeleteNoticeMessage] = useState("");
  const [expandedOccasions, setExpandedOccasions] = useState({});
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [savedBusy, setSavedBusy] = useState({});
  const [historyBusy, setHistoryBusy] = useState({});
  const [showAllSaved, setShowAllSaved] = useState(false);
  const SAVED_CAP = 5;
  const [showOlderHistory, setShowOlderHistory] = useState(false);
  const [expandedHistoryYears, setExpandedHistoryYears] = useState({});
  const [shareOccasion, setShareOccasion] = useState(null);
  const [shareMembers, setShareMembers] = useState([]);
  const [shareTargetId, setShareTargetId] = useState("");
  const [shareError, setShareError] = useState(null);
  const [shareSaving, setShareSaving] = useState(false);
  const [showProfileActions, setShowProfileActions] = useState(false);
  const [actionOccasion, setActionOccasion] = useState(null);
  const [confirmDeletePerson, setConfirmDeletePerson] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(() => typeof window !== "undefined" ? window.innerWidth >= 640 : true);
  const actionPresentation = useFormPresentation();
  const profileSheetRef = useRef(null);
  const profileSheetDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const occasionSheetRef = useRef(null);
  const occasionSheetDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const occasionsRef = useRef(null);
  const showLoading = useMinLoad(loading);
  const showSavingNotice = useMinLoad(savingOcc, 700);
  const showDeleteNotice = useMinLoad(Boolean(deleteNoticeMessage), 700);

  useEffect(() => {
    if (deletingOccasionId) {
      setDeleteNoticeMessage("Removing occasion...");
    }
  }, [deletingOccasionId]);

  useEffect(() => {
    const removingHistory = Object.values(historyBusy).some(Boolean);
    if (removingHistory) {
      setDeleteNoticeMessage("Removing gift history...");
    } else if (!deletingOccasionId) {
      setDeleteNoticeMessage("");
    }
  }, [historyBusy, deletingOccasionId]);

  useOnEscape(useCallback(() => {
    if (actionOccasion) setActionOccasion(null);
    else if (showProfileActions) setShowProfileActions(false);
    else if (shareOccasion) setShareOccasion(null);
    else if (confirmDeleteOcc) setConfirmDeleteOcc(null);
    else if (confirmDeleteHistory) setConfirmDeleteHistory(null);
    else if (confirmDeletePerson) setConfirmDeletePerson(false);
    else if (showOccForm) {
      setShowOccForm(false);
      setEditingOccasion(null);
    }
  }, [actionOccasion, showProfileActions, shareOccasion, confirmDeleteOcc, confirmDeleteHistory, confirmDeletePerson, showOccForm]));

  useEffect(() => {
    Promise.all([
      peopleApi.get(personId),
      peopleApi.getHistory(personId),
      giftsApi.getSavedForPerson(personId).catch(() => []),
    ]).then(([p, h, saved]) => {
      setPerson(p);
      setHistory(h);
      setSavedIdeas(saved || []);
    }).finally(() => setLoading(false));
  }, [personId]);

  const handleSavedPurchase = async (suggestionId) => {
    setSavedBusy(b => ({ ...b, [suggestionId]: true }));
    try {
      await giftsApi.markPurchased(suggestionId);
      setSavedIdeas(ideas => ideas.filter(s => s.id !== suggestionId));
      // Refresh history
      const h = await peopleApi.getHistory(personId).catch(() => history);
      setHistory(h);
      toast?.("Marked as purchased", "success");
    } finally {
      setSavedBusy(b => ({ ...b, [suggestionId]: false }));
    }
  };

  const handleSavedUnsave = async (suggestionId) => {
    setSavedBusy(b => ({ ...b, [suggestionId]: true }));
    try {
      await giftsApi.unsave(suggestionId);
      setSavedIdeas(ideas => ideas.filter(s => s.id !== suggestionId));
    } finally {
      setSavedBusy(b => ({ ...b, [suggestionId]: false }));
    }
  };

  const handleDeleteHistory = async (historyId) => {
    setHistoryBusy(b => ({ ...b, [historyId]: true }));
    try {
      await peopleApi.deleteHistory(personId, historyId);
      setHistory(entries => entries.filter(entry => entry.id !== historyId));
      toast?.("Gift history removed", "success");
    } catch (err) {
      toast?.(err?.data?.error || err?.message || "Could not remove this gift history right now.", "error");
    } finally {
      setHistoryBusy(b => {
        const next = { ...b };
        delete next[historyId];
        return next;
      });
    }
  };

  const handleAddOccasion = async (data) => {
    setSavingOcc(true);
    try {
      let savedOccasion = null;
      if (editingOccasion) {
        savedOccasion = await occasionsApi.update(editingOccasion.id, data);
      } else {
        savedOccasion = await occasionsApi.create(data);
      }
      const updated = await peopleApi.get(personId);
      setPerson(updated);
      setShowOccForm(false);
      setEditingOccasion(null);
      if (!editingOccasion && savedOccasion?.id) {
        setExpandedOccasions(current => ({ ...current, [savedOccasion.id]: true }));
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("occasion_id", String(savedOccasion.id));
        setSearchParams(nextParams, { replace: true });
      }
      toast?.(editingOccasion ? "Occasion updated" : "Occasion added", "success");
    } finally {
      setSavingOcc(false);
    }
  };

  const handleDeleteOccasion = async (occId) => {
    setDeletingOccasionId(occId);
    try {
      await occasionsApi.delete(occId);
      const updated = await peopleApi.get(personId);
      setPerson(updated);
      toast?.("Occasion removed", "success");
    } catch (err) {
      toast?.(err?.data?.error || err?.message || "Could not remove this occasion right now.", "error");
    } finally {
      setDeletingOccasionId(null);
      setDeleteNoticeMessage("");
    }
  };

  const focusedOccasionId = Number(searchParams.get("occasion_id")) || null;
  const { upcomingOccasions, pastOccasions, sortedOccasions } = useMemo(() => {
    const allOccasions = dedupeEventGiftOccasions(
      (person?.occasions || []).filter(occasion => isPersonOccasionType(occasion.occasion_type))
    );
    const upcoming = [...allOccasions]
      .filter(occasion => occasion.next_occurrence || occasion.days_until !== null)
      .sort((a, b) => (a.days_until ?? 9999) - (b.days_until ?? 9999));
    const past = [...allOccasions]
      .filter(occasion => !occasion.next_occurrence && occasion.days_until === null)
      .sort((a, b) => new Date(b.occasion_date) - new Date(a.occasion_date));
    return {
      upcomingOccasions: upcoming,
      pastOccasions: past,
      sortedOccasions: [...upcoming, ...past],
    };
  }, [person?.occasions]);

  useEffect(() => {
    if (!focusedOccasionId || !sortedOccasions.some(occasion => occasion.id === focusedOccasionId)) return;
    setExpandedOccasions(current => (
      current[focusedOccasionId]
        ? current
        : { ...current, [focusedOccasionId]: true }
    ));
  }, [focusedOccasionId, sortedOccasions]);

  useEffect(() => {
    if (searchParams.get("add_occasion") !== "1") return;
    setEditingOccasion(null);
    setShowOccForm(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("add_occasion");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("section") !== "occasions") return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("section");
    setSearchParams(nextParams, { replace: true });
    window.setTimeout(() => {
      occasionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!focusedOccasionId || !expandedOccasions[focusedOccasionId]) return;
    const match = document.querySelector(`[data-occasion-id="${focusedOccasionId}"]`);
    if (!match) return;
    match.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("occasion_id");
      setSearchParams(nextParams, { replace: true });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusedOccasionId, expandedOccasions, searchParams, setSearchParams]);

  const openGiftAsk = useCallback((question = "") => {
    const clean = String(question || "").trim();
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: clean || `Ask about gifts for ${person?.name || "this person"}.`,
        context: {
          module: "people",
          module_label: "People & Gifts",
          surface: person?.name ? `Person detail: ${person.name}` : "Person detail",
          summary: person?.name
            ? `Person: ${person.name}; relationship: ${person.relationship || "not saved"}; interests: ${(person.interests || "").slice(0, 180) || "not saved"}; dislikes: ${(person.dislikes || "").slice(0, 140) || "not saved"}`
            : "",
        },
      },
    }));
  }, [person]);

  if (showLoading) return (
    <div className="loading-spinner-center">
      <span className="spinner" />
      Loading profile…
    </div>
  );
  if (!person) return <div className="loading-spinner-center">Person not found</div>;
  const currentWorkspace = user?.workspaces?.find(workspace => workspace.id === user?.active_workspace_id) || user?.workspace || null;
  const hasFamilyAccess = user?.plan === "family" || (user?.workspaces || []).some(workspace => workspace.plan === "family");
  const showFamilyPrivacyNotice = hasFamilyAccess || currentWorkspace?.plan === "family";
  const canShareFromHere = hasFamilyAccess && (currentWorkspace?.workspace_type || "personal") === "personal";

  const [bg, fg] = AVATAR_COLORS[person.name.charCodeAt(0) % AVATAR_COLORS.length];
  const initials = person.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hasUploadedPhoto = typeof person.photo_url === "string" && (
    person.photo_url.startsWith("data:image/") ||
    person.photo_url.startsWith("https://") ||
    person.photo_url.startsWith("http://")
  );

  const groupedHistory = history.reduce((acc, entry) => {
    const key = entry.year || "Earlier";
    acc[key] = acc[key] || [];
    acc[key].push(entry);
    return acc;
  }, {});
  const historyYears = Object.keys(groupedHistory).sort((a, b) => Number(b) - Number(a));
  const recentHistoryYears = historyYears.slice(0, 3);
  const olderHistoryYears = historyYears.slice(3);

  const REACTION_COLORS = {
    loved: ["#EAF3EE", "#4A7C59"],
    liked: ["#FDF6E3", "#C9A84C"],
    neutral: ["var(--cream)", "var(--ink-muted)"],
    disliked: ["var(--rose-light)", "var(--rose-dark)"],
  };

  const renderTags = (title, tags) => (
    tags?.length ? (
      <div className="person-profile-section mt-8">
        <div className="text-sm person-profile-section-title" style={{ fontWeight: 500, marginBottom: 6 }}>{title}</div>
        <div className="person-profile-tag-list" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {tags.map(tag => (
            <span key={tag} className="badge person-profile-tag" style={{ background: "var(--cream)", color: "var(--ink-soft)", border: "1px solid var(--border)" }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    ) : null
  );

  const MEMORY_EMOJIS = {
    "Favorite foods": "🍽️",
    "Favorite stores": "🛍️",
    "Gift preferences": "🎁",
    "Sizes": "📏",
    "Allergies / dietary needs": "⚠️",
    "Favorite restaurants": "🍴",
    "Traditions": "🎊",
  };

  const preferenceGroups = [
    { key: "interests", icon: "❤️", title: "Interests", tags: person.interest_tags, note: "" },
    { key: "hobbies", icon: "🎯", title: "Hobbies", tags: person.hobby_tags, note: "" },
    { key: "avoid", icon: "🚫", title: "Avoid", tags: person.dislike_tags, note: person.dislikes },
  ].map(group => {
    const values = [
      ...(group.tags || []),
      ...(group.note ? [group.note] : []),
    ].filter(Boolean);
    return {
      ...group,
      values,
      visibleValues: values.slice(0, 4),
      hiddenValues: values.slice(4),
    };
  }).filter(group => group.values.length > 0);

  const previewPreference = (values, hasMore) => {
    const text = values.join(", ");
    if (!text) return "";
    const preview = text.length > 34 ? `${text.slice(0, 34).trim()}` : text;
    return hasMore ? `${preview}...` : preview;
  };

  const memoryEntries = [
    ["Favorite foods", person.favorite_foods],
    ["Favorite stores", person.favorite_stores],
    ["Gift preferences", person.gift_preferences],
    ["Sizes", person.sizes],
    ["Allergies / dietary needs", person.allergies],
    ["Favorite restaurants", person.favorite_restaurants],
    ["Traditions", person.traditions],
  ].filter(([, value]) => String(value || "").trim());

  const openAddOccasion = () => {
    setEditingOccasion(null);
    setShowOccForm(true);
    window.setTimeout(() => {
      document.getElementById("person-occasion-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const openEditOccasion = (occasion) => {
    if (EVENT_OCCASION_TYPES.has(occasion.occasion_type)) {
      navigate(`/app/events?edit_event_id=${occasion.id}`);
      return;
    }
    setEditingOccasion(occasion);
    setShowOccForm(true);
    window.setTimeout(() => {
      document.getElementById("person-occasion-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const openOccasionPrimaryAction = (occasion) => {
    if (occasion.occasion_type === "trip") {
      const dest = occasion.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim() || "";
      const style = occasion.notes?.match(/Style: ([^|]+)/)?.[1]?.trim() || "";
      const endDate = occasion.notes?.match(/End date: ([^|]+)/)?.[1]?.trim() || "";
      let duration = 7;
      if (endDate && occasion.occasion_date) {
        const start = new Date(occasion.occasion_date);
        const end = new Date(endDate);
        duration = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
      }
      navigate(`/app/trip/occasion/${occasion.id}?destination=${encodeURIComponent(dest)}&style=${encodeURIComponent(style)}&duration=${duration}&travelers=${encodeURIComponent(person.name)}`);
      return;
    }
    if (isGiftOccasionType(occasion.occasion_type)) {
      navigate(`/app/people/${personId}/gifts/${occasion.id}`);
    }
  };

  function closeProfileActions() {
    if (profileSheetRef.current) {
      profileSheetRef.current.style.transform = "";
      profileSheetRef.current.style.transition = "";
    }
    setShowProfileActions(false);
  }


  function closeActionOccasion() {
    if (occasionSheetRef.current) {
      occasionSheetRef.current.style.transform = "";
      occasionSheetRef.current.style.transition = "";
    }
    setActionOccasion(null);
  }

  function openOccasionActions(occasion) {
    if (actionPresentation === "modal") {
      setExpandedOccasions(prev => ({ ...prev, [occasion.id]: !prev[occasion.id] }));
      return;
    }
    setActionOccasion(occasion);
  }

  function startOccasionSheetDrag(event) {
    if (!actionOccasion) return;
    occasionSheetDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (occasionSheetRef.current) occasionSheetRef.current.style.transition = "none";
  }

  function moveOccasionSheetDrag(event) {
    if (!occasionSheetDragRef.current.active || !occasionSheetRef.current) return;
    const delta = Math.max(0, event.clientY - occasionSheetDragRef.current.startY);
    occasionSheetDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    occasionSheetRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endOccasionSheetDrag(event) {
    if (!occasionSheetDragRef.current.active || !occasionSheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = occasionSheetDragRef.current.currentY;
    occasionSheetDragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > 72) {
      occasionSheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      occasionSheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(closeActionOccasion, 205);
      return;
    }
    occasionSheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    occasionSheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!occasionSheetRef.current || occasionSheetDragRef.current.active) return;
      occasionSheetRef.current.style.transform = "";
      occasionSheetRef.current.style.transition = "";
    }, 190);
  }

  function startProfileSheetDrag(event) {
    if (!showProfileActions) return;
    profileSheetDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (profileSheetRef.current) profileSheetRef.current.style.transition = "none";
  }

  function moveProfileSheetDrag(event) {
    if (!profileSheetDragRef.current.active || !profileSheetRef.current) return;
    const delta = Math.max(0, event.clientY - profileSheetDragRef.current.startY);
    profileSheetDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    profileSheetRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endProfileSheetDrag(event) {
    if (!profileSheetDragRef.current.active || !profileSheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = profileSheetDragRef.current.currentY;
    profileSheetDragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > 72) {
      profileSheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      profileSheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(closeProfileActions, 205);
      return;
    }
    profileSheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    profileSheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!profileSheetRef.current || profileSheetDragRef.current.active) return;
      profileSheetRef.current.style.transform = "";
      profileSheetRef.current.style.transition = "";
    }, 190);
  }

  const toggleHistoryYear = (year) => {
    setExpandedHistoryYears(current => ({
      ...current,
      [year]: !current[year],
    }));
  };

  const openShareOccasion = async (occasion) => {
    setShareOccasion(occasion);
    setShareMembers([]);
    setShareTargetId("");
    setShareError(null);
    try {
      const data = await sharingApi.members();
      const members = Array.isArray(data.members) ? data.members : [];
      setShareMembers(members);
      setShareTargetId(members[0]?.user_id ? String(members[0].user_id) : "");
      if (members.length === 0) setShareError("Add another Family member in Settings before sharing.");
    } catch (err) {
      setShareError(err.message || "Could not load Family members.");
    }
  };

  const handleShareOccasion = async () => {
    if (!shareOccasion || !shareTargetId) return;
    setShareSaving(true);
    setShareError(null);
    try {
      await sharingApi.create({
        resource_type: "occasion",
        resource_id: shareOccasion.id,
        recipient_user_id: Number(shareTargetId),
      });
      const member = shareMembers.find(item => String(item.user_id) === String(shareTargetId));
      toast?.(`Shared with ${member?.full_name || "Family member"}`, "success");
      setShareOccasion(null);
    } catch (err) {
      setShareError(err.message || "Could not share this occasion.");
    } finally {
      setShareSaving(false);
    }
  };

  return (
    <div className={`hm-desktop-page-shell hm-gifts-person-detail${embeddedSheet ? " hm-gifts-person-detail--embedded" : ""}`}>
      <button
        className="btn btn-ghost btn-sm mb-16"
        onClick={() => {
          if (embeddedSheet && onCloseEmbedded) {
            onCloseEmbedded();
            return;
          }
          navigate("/app/people");
        }}
      >
        ← All people
      </button>

      {/* Header */}
      <div className="card mb-16 person-profile-card" style={{ "--pd-accent": bg }}>
        {/* Coloured accent bar */}
        <div className="pd-hero-accent-bar" style={{ background: `linear-gradient(90deg, ${bg} 0%, transparent 100%)` }} />

        {/* Identity row */}
        <div className="pd-hero">
          <div
            className="pd-hero-avatar"
            style={{
              background: hasUploadedPhoto ? "transparent" : bg,
              color: fg,
              boxShadow: `0 0 0 3px #fff, 0 0 0 5px ${bg}, 0 4px 12px rgba(0,0,0,0.12)`,
            }}
          >
            {hasUploadedPhoto
              ? <img src={person.photo_url} alt={person.name} className="pd-hero-avatar-img" />
              : initials}
          </div>
          <div className="pd-hero-info">
            <div className="pd-hero-name-row">
              <h2 className="pd-hero-name">{person.name}</h2>
              <div className="pd-hero-actions">
                <button
                  type="button"
                  className="pd-hero-edit-btn"
                  onClick={() => navigate("/app/people", { state: { edit: person.id } })}
                  aria-label="Edit profile"
                  title="Edit profile"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  className="pd-hero-more-btn"
                  onClick={() => setShowProfileActions(true)}
                  aria-label="Profile actions"
                >
                  ⋯
                </button>
              </div>
            </div>
            <div className="pd-hero-meta">
              {[
                person.relationship,
                person.age ? `${person.age} yrs` : null,
                `Budget $${person.budget_min}–$${person.budget_max}`,
              ].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>

        {/* Stats strip */}
        {(person.annual_spend > 0 || person.gift_count > 0 || person.last_gift_name) && (
          <div className="pd-hero-stats">
            {person.annual_spend > 0 && (
              <div className="pd-hero-stat">
                <strong className="pd-hero-stat-value pd-hero-stat-value--accent">
                  ${Number(person.annual_spend).toFixed(0)}
                </strong>
                <span className="pd-hero-stat-label">This year</span>
              </div>
            )}
            {person.gift_count > 0 && (
              <div className="pd-hero-stat">
                <strong className="pd-hero-stat-value">{person.gift_count}</strong>
                <span className="pd-hero-stat-label">Gift{person.gift_count !== 1 ? "s" : ""} total</span>
              </div>
            )}
            {person.last_gift_name && (
              <div className="pd-hero-stat pd-hero-stat--last">
                <strong className="pd-hero-stat-value" title={person.last_gift_name}>
                  {person.last_gift_name}
                </strong>
                <span className="pd-hero-stat-label">Last gift</span>
              </div>
            )}
          </div>
        )}

        {/* Mobile collapse toggle */}
          <button
            type="button"
            className="pd-profile-toggle"
            onClick={() => setProfileExpanded(prev => !prev)}
            aria-expanded={profileExpanded}
          >
            <span>{profileExpanded ? `Hide details` : `About ${person.name}`}</span>
            <span className="pd-profile-toggle-chevron">{profileExpanded ? "↑" : "↓"}</span>
          </button>

          {/* Expandable profile details */}
          <div className={`pd-profile-expandable${profileExpanded ? " is-open" : ""}`}>

            {/* Desktop tag grid */}
            <div className="person-profile-details-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 14 }}>
              <div className="person-profile-details-col">
                {renderTags("❤️ Interests", person.interest_tags)}
              </div>
              <div className="person-profile-details-col">
                {renderTags("🎯 Hobbies", person.hobby_tags)}
              </div>
              <div className="person-profile-details-col">
                {renderTags("🚫 Avoid", person.dislike_tags)}
                {person.dislikes && (
                  <div className="person-profile-section mt-8">
                    <div className="text-sm person-profile-section-title" style={{ fontWeight: 500, marginBottom: 4 }}>Extra avoid notes</div>
                    <p className="text-sm text-muted person-profile-note">{person.dislikes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {person.notes && (
              <div className="person-profile-section person-profile-notes-section" style={{ marginTop: 12 }}>
                <div className="text-sm person-profile-section-title" style={{ fontWeight: 500, marginBottom: 4 }}>📝 Notes</div>
                <p className="text-sm text-muted person-profile-note">{person.notes}</p>
              </div>
            )}

            {/* Mobile accordion preferences */}
            {preferenceGroups.length > 0 && (
              <div className="person-mobile-preferences" aria-label="Preferences">
                {preferenceGroups.map(group => (
                  <details
                    key={group.key}
                    className={`person-mobile-pref-row person-mobile-pref-row--${group.key}`}
                  >
                    <summary>
                      <span className="person-mobile-pref-icon">{group.icon}</span>
                      <span className="person-mobile-pref-copy">
                        <strong>{group.title}</strong>
                        <em className="person-mobile-pref-preview">{previewPreference(group.visibleValues, group.hiddenValues.length > 0)}</em>
                      </span>
                      <span className="person-mobile-pref-count">{group.values.length}</span>
                    </summary>
                    {/* Show ALL tags when expanded */}
                    <div className="person-mobile-pref-tags">
                      {group.values.map(value => (
                        <span key={value} className="badge person-profile-tag">{value}</span>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {/* Relationship memory — redesigned */}
            {memoryEntries.length > 0 && (
              <div className="pd-memory-section">
                <div className="pd-memory-title">🧠 Relationship memory</div>
                <div className="pd-memory-list">
                  {memoryEntries.map(([label, value]) => (
                    <div key={label} className="pd-memory-item">
                      <span className="pd-memory-item-emoji">{MEMORY_EMOJIS[label] || "📝"}</span>
                      <div className="pd-memory-item-body">
                        <div className="pd-memory-item-label">{label}</div>
                        <div className="pd-memory-item-value">{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>{/* end pd-profile-expandable */}
      </div>{/* end person-profile-card */}


      {showProfileActions && (() => {
        const content = (
          <>
            {actionPresentation === "sheet" && (
              <GiftSheetTopRow
                onClose={closeProfileActions}
                closeLabel="Close profile actions"
                handleProps={{
                  onPointerDown: startProfileSheetDrag,
                  onPointerMove: moveProfileSheetDrag,
                  onPointerUp: endProfileSheetDrag,
                  onPointerCancel: endProfileSheetDrag,
                  "aria-label": "Drag down to close",
                }}
              />
            )}
            <GiftSheetHeader title={person.name} icon="👤" subtitle="More profile actions" />
            <div className="hm-gifts-sheet-actions">
              <button type="button" onClick={() => { closeProfileActions(); navigate("/app/people", { state: { edit: person.id } }); }}>
                <span>✏️</span>
                <span><strong>Edit profile</strong><small>Update interests, budget, relationship, and memory</small></span>
                <em>›</em>
              </button>
              {canShareFromHere && (
                <button type="button" onClick={() => { closeProfileActions(); navigate("/app/family"); }}>
                  <span>👥</span>
                  <span><strong>Share with Family</strong><small>Manage shared People & Gifts items</small></span>
                  <em>›</em>
                </button>
              )}
              <button type="button" className="is-danger" onClick={() => { closeProfileActions(); setConfirmDeletePerson(true); }}>
                <span>🗑️</span>
                <span><strong>Remove person</strong><small>Delete this profile, occasions, and history</small></span>
              </button>
            </div>
          </>
        );
        return actionPresentation === "modal" ? (
          <CenteredModal title={person.name} onClose={closeProfileActions}>
            {content}
          </CenteredModal>
        ) : (
          <div className="hm-gifts-sheet-overlay" onClick={closeProfileActions}>
      <BackButtonTrap onClose={closeProfileActions} />
            <div
              className="hm-gifts-action-sheet"
              ref={profileSheetRef}
              onClick={event => event.stopPropagation()}
            >
              {content}
            </div>
          </div>
        );
      })()}

      {actionOccasion && (() => {
        const content = (
          <>
            {actionPresentation === "sheet" && (
              <GiftSheetTopRow
                onClose={closeActionOccasion}
                closeLabel="Close occasion actions"
                handleProps={{
                  onPointerDown: startOccasionSheetDrag,
                  onPointerMove: moveOccasionSheetDrag,
                  onPointerUp: endOccasionSheetDrag,
                  onPointerCancel: endOccasionSheetDrag,
                  "aria-label": "Drag down to close",
                }}
              />
            )}
            <GiftSheetHeader
              title={actionOccasion.occasion_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
              icon={OCCASION_ICONS[actionOccasion.occasion_type] || "📅"}
              subtitle={[person.name, actionOccasion.days_until !== null ? `${actionOccasion.days_until} days` : null]
                .filter(Boolean).join(" · ")}
            />
            {/* Info strip — Yearly · Last · Budget */}
            {(() => {
              const lastDate = getLastCelebratedDate(actionOccasion);
              const hasOverride = actionOccasion.budget_override_min || actionOccasion.budget_override_max;
              const chips = [
                actionOccasion.is_recurring ? "🔁 Yearly" : null,
                lastDate ? `🕐 Last: ${lastDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : null,
                hasOverride ? `💰 Budget $${actionOccasion.budget_override_min || person.budget_min}–$${actionOccasion.budget_override_max || person.budget_max}` : null,
              ].filter(Boolean);
              if (!chips.length) return null;
              return (
                <div className="occ-sheet-info-strip">
                  {chips.map(chip => <span key={chip} className="occ-sheet-info-chip">{chip}</span>)}
                </div>
              );
            })()}

            <div className="hm-gifts-sheet-actions">
              {(actionOccasion.occasion_type === "trip" || isGiftOccasionType(actionOccasion.occasion_type)) && (
                <button type="button" onClick={() => { const occ = actionOccasion; closeActionOccasion(); openOccasionPrimaryAction(occ); }}>
                  <span>{actionOccasion.occasion_type === "trip" ? "✈️" : "🎁"}</span>
                  <span>
                    <strong>
                      {actionOccasion.occasion_type === "trip"
                        ? (actionOccasion.has_trip_plan ? "View trip plan" : "Plan trip")
                        : (actionOccasion.has_suggestions ? "View gift ideas" : "Get gift ideas")}
                    </strong>
                    <small>
                      {actionOccasion.occasion_type === "trip"
                        ? "Open the trip flow for this occasion"
                        : `Gift ideas for ${person.name}'s occasion`}
                    </small>
                  </span>
                  <em>›</em>
                </button>
              )}
              <button type="button" onClick={() => { const occ = actionOccasion; closeActionOccasion(); openEditOccasion(occ); }}>
                <span>✏️</span>
                <span><strong>Edit occasion</strong><small>Update date, budget, recurrence, or notes</small></span>
                <em>›</em>
              </button>
              <button type="button" className="is-danger" onClick={() => { setConfirmDeleteOcc(actionOccasion); closeActionOccasion(); }}>
                <span>🗑️</span>
                <span><strong>Remove occasion</strong></span>
              </button>
            </div>
          </>
        );
        return actionPresentation === "modal" ? (
          <CenteredModal
            title={actionOccasion.occasion_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            onClose={closeActionOccasion}
          >
            {content}
          </CenteredModal>
        ) : (
          <div className="hm-gifts-sheet-overlay" onClick={closeActionOccasion}>
            <BackButtonTrap onClose={closeActionOccasion} />
            <div
              className="hm-gifts-action-sheet"
              ref={occasionSheetRef}
              onClick={event => event.stopPropagation()}
            >
              {content}
            </div>
          </div>
        );
      })()}

      {/* Occasions */}
      <div className="flex-between mb-16 person-occasion-header" ref={occasionsRef}>
        <h3 style={{ fontSize: 20 }}>🎉 Occasions</h3>
        <button className="btn btn-primary btn-sm person-occasion-add-inline" onClick={() => {
          if (showOccForm) {
            setShowOccForm(false);
            setEditingOccasion(null);
          } else {
            openAddOccasion();
          }
        }}>
          {showOccForm ? "Cancel" : "+ Add occasion"}
        </button>
      </div>

      {showOccForm && (
        <FormPresentation
          title={editingOccasion ? "Edit occasion" : "Add occasion"}
          onClose={() => { setShowOccForm(false); setEditingOccasion(null); }}
        >
          <OccasionForm
            personId={person.id}
            initial={editingOccasion}
            onSave={handleAddOccasion}
            onCancel={() => { setShowOccForm(false); setEditingOccasion(null); }}
            saving={savingOcc}
          />
        </FormPresentation>
      )}

      {sortedOccasions.length === 0 ? (
        <div className="card mb-24" style={{ textAlign: "center", padding: "32px" }}>
          <p className="text-muted">No occasions yet.</p>
        </div>
      ) : (
        <>
          {upcomingOccasions.length > 0 && (
            <div className="person-occasion-section" style={{ marginBottom: 28 }}>
              <h4 className="subsection-title person-occasion-heading">Upcoming</h4>
              <div className="person-occasion-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {upcomingOccasions.map(occ => (
                  <div
                    key={occ.id}
                    className={`card card-sm person-occasion-card ${occ.has_suggestions ? "is-ready" : urgencyClass(occ.days_until)}`}
                    data-occasion-id={occ.id}
                    style={{
                      padding: 0,
                      overflow: "hidden",
                      border: focusedOccasionId === occ.id ? "1px solid var(--rose)" : undefined,
                      boxShadow: focusedOccasionId === occ.id ? "0 0 0 2px rgba(231, 97, 74, 0.08)" : undefined,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => openOccasionActions(occ)}
                      className="person-occasion-trigger"
                      style={{
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        textAlign: "left",
                        padding: "14px 16px",
                        cursor: "pointer",
                        color: "var(--ink-soft)",
                      }}
                    >
                      <div className="flex-between" style={{ gap: 16, alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 20, flexShrink: 0 }}>
                            {OCCASION_ICONS[occ.occasion_type] || "📅"}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
                              {occ.occasion_type.replace(/_/g, " ")}
                            </div>
                            <div className="text-sm text-muted mt-8">
                              {[
                                occ.next_occurrence
                                  ? new Date(occ.next_occurrence + "T00:00:00").toLocaleDateString("en-US", {
                                      weekday: "short", month: "long", day: "numeric"
                                    })
                                  : "One-time passed",
                                occ.is_recurring ? "Yearly" : null,
                              ].filter(Boolean).join(", ")}
                            </div>
                          </div>
                        </div>
                        <div className="person-occasion-side">
                          {occ.days_until !== null && (
                            <span className={`days-pill ${occ.days_until <= 14 ? "days-urgent" : occ.days_until <= 30 ? "days-soon" : "days-ok"}`}>
                              {occ.days_until === 0 ? "Today!" : occ.days_until === 1 ? "Tomorrow" : `${occ.days_until}d`}
                            </span>
                          )}
                          {isGiftOccasionType(occ.occasion_type) && (
                            <span className={`person-occasion-gift-state ${occ.has_suggestions ? "is-ready" : "is-empty"}`}>
                              {occ.has_suggestions ? "✓ Gift ready" : "✦ Get ideas →"}
                            </span>
                          )}
                          <span className="person-occasion-expand-indicator" style={{ color: "var(--ink-hint)", fontSize: 14 }}>{expandedOccasions[occ.id] ? "▲" : "▼"}</span>
                        </div>
                      </div>
                    </button>

                    {expandedOccasions[occ.id] && (
                      <div className="person-occasion-detail" style={{ padding: "0 16px 16px", borderTop: "1px solid var(--border)" }}>
                        <div className="person-occasion-badges" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14, marginBottom: 12 }}>
                          {occ.is_recurring && (
                            <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-soft)" }}>
                              Yearly
                            </span>
                          )}
                          {getLastCelebratedDate(occ) && (
                            <span className="badge" style={{ background: "var(--warm-white)", color: "var(--ink-muted)", border: "1px solid var(--border)" }}>
                              Last: {getLastCelebratedDate(occ).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                          {(occ.budget_override_min || occ.budget_override_max) && (
                            <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-soft)" }}>
                              Budget ${occ.budget_override_min || person.budget_min}–${occ.budget_override_max || person.budget_max}
                            </span>
                          )}
                        </div>
                        {occ.notes && (
                          <div className="text-sm text-muted person-occasion-notes" style={{ marginBottom: 12 }}>
                            {occ.notes}
                          </div>
                        )}
                        <div className="flex-center gap-8 person-occasion-actions" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                          {occ.occasion_type === "trip" ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openOccasionPrimaryAction(occ)}
                            >
                              {occ.has_trip_plan ? "View trip plan" : "Plan trip"}
                            </button>
                          ) : isGiftOccasionType(occ.occasion_type) ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => navigate(`/app/people/${personId}/gifts/${occ.id}`)}
                            >
                              {occ.has_suggestions ? "View gift ideas" : "Get gifts"}
                            </button>
                          ) : null}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEditOccasion(occ)}
                          >
                            ✏️ Edit
                          </button>
                          {canShareFromHere && (
                            <button
                              className="btn btn-secondary btn-sm person-occasion-share-action"
                              onClick={() => openShareOccasion(occ)}
                            >
                              👥 Share
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: "var(--rose)" }}
                            aria-label="Remove occasion"
                            onClick={() => setConfirmDeleteOcc(occ)}
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {pastOccasions.length > 0 && (
            <div className="person-occasion-section is-past" style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32 }}>
              <h4 className="subsection-title person-occasion-heading">Past occasions</h4>
              {pastOccasions.map(occ => (
                <div
                  key={occ.id}
                  className="card card-sm person-occasion-card is-past"
                  data-occasion-id={occ.id}
                  style={{
                    padding: 0,
                    overflow: "hidden",
                    border: focusedOccasionId === occ.id ? "1px solid var(--rose)" : undefined,
                    boxShadow: focusedOccasionId === occ.id ? "0 0 0 2px rgba(231, 97, 74, 0.08)" : undefined,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openOccasionActions(occ)}
                    className="person-occasion-trigger"
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: "none",
                      textAlign: "left",
                      padding: "14px 16px",
                      cursor: "pointer",
                      color: "var(--ink-soft)",
                    }}
                  >
                    <div className="flex-between" style={{ gap: 16, alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 20, flexShrink: 0 }}>
                          {OCCASION_ICONS[occ.occasion_type] || "📅"}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
                            {occ.occasion_type.replace(/_/g, " ")}
                          </div>
                          <div className="text-sm text-muted mt-8">
                            {new Date(occ.occasion_date + "T00:00:00").toLocaleDateString("en-US", {
                              weekday: "short", month: "long", day: "numeric", year: "numeric"
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="person-occasion-side">
                        <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-muted)" }}>
                          Past
                        </span>
                        <span className="person-occasion-expand-indicator" style={{ color: "var(--ink-hint)", fontSize: 14 }}>{expandedOccasions[occ.id] ? "▲" : "▼"}</span>
                      </div>
                    </div>
                  </button>

                  {expandedOccasions[occ.id] && (
                    <div className="person-occasion-detail" style={{ padding: "0 16px 16px", borderTop: "1px solid var(--border)" }}>
                      {(occ.budget_override_min || occ.budget_override_max) && (
                        <div className="person-occasion-badges person-occasion-budget-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14, marginBottom: 12 }}>
                          <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-soft)" }}>
                            Budget ${occ.budget_override_min || person.budget_min}–${occ.budget_override_max || person.budget_max}
                          </span>
                        </div>
                      )}
                      {occ.notes && (
                        <div className="text-sm text-muted person-occasion-notes" style={{ marginBottom: 12, marginTop: 14 }}>
                          {occ.notes}
                        </div>
                      )}
                      <div className="flex-center gap-8 person-occasion-actions" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                        {occ.occasion_type === "trip" ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => openOccasionPrimaryAction(occ)}
                          >
                            {occ.has_trip_plan ? "View trip plan" : "Open trip"}
                          </button>
                        ) : isGiftOccasionType(occ.occasion_type) ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate(`/app/people/${personId}/gifts/${occ.id}`)}
                          >
                            {occ.has_suggestions ? "View gift ideas" : "Open gift ideas"}
                          </button>
                        ) : null}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditOccasion(occ)}
                        >
                          ✏️ Edit
                        </button>
                        {canShareFromHere && (
                          <button
                            className="btn btn-secondary btn-sm person-occasion-share-action"
                            onClick={() => openShareOccasion(occ)}
                          >
                            👥 Share
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--rose)" }}
                          aria-label="Remove occasion"
                          onClick={() => setConfirmDeleteOcc(occ)}
                        >
                          🗑️ Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {shareOccasion && (
        <div className="modal-shell">
          <div className="card modal-card" style={{ maxWidth: 460 }}>
            <div className="flex-between mb-16">
              <h3 style={{ fontSize: 18 }}>👥 Share with Family</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShareOccasion(null); setShareError(null); }}>x</button>
            </div>
            <div className="notice-banner mb-16">
              <p>
                This shares only this occasion for {person.name}. Gift ideas, saved gifts, purchase status, and gift history stay private.
              </p>
            </div>
            {shareError && (
              <div className="notice-banner is-error mb-16">
                <p>{shareError}</p>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Family member</label>
              <select className="form-select" value={shareTargetId} onChange={e => setShareTargetId(e.target.value)}>
                {shareMembers.map(member => (
                  <option key={member.user_id} value={member.user_id}>{member.full_name} - {member.email}</option>
                ))}
              </select>
            </div>
            <div className="flex-between" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShareOccasion(null); setShareError(null); }} disabled={shareSaving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleShareOccasion} disabled={shareSaving || !shareTargetId}>
                {shareSaving ? "Sharing..." : "Share with Family"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete occasion */}
      {confirmDeleteOcc && (
        <ConfirmModal
          tag="⚠ Remove occasion"
          title={`Remove ${confirmDeleteOcc.occasion_type.replace(/_/g, " ")}?`}
          message="Saved gift ideas for this occasion will also be removed."
          confirmLabel="Remove"
          onCancel={() => setConfirmDeleteOcc(null)}
          onConfirm={async () => {
            const occ = confirmDeleteOcc;
            setConfirmDeleteOcc(null);
            await handleDeleteOccasion(occ.id);
          }}
          loading={deletingOccasionId === confirmDeleteOcc.id}
        />
      )}

      {confirmDeleteHistory && (
        <ConfirmModal
          tag="⚠ Remove gift"
          title={`Remove from ${person.name}'s history?`}
          message="HouseMind uses gift history to avoid suggesting the same gift again."
          confirmLabel="Remove"
          onCancel={() => setConfirmDeleteHistory(null)}
          onConfirm={async () => {
            const entry = confirmDeleteHistory;
            setConfirmDeleteHistory(null);
            await handleDeleteHistory(entry.id);
          }}
          loading={Boolean(historyBusy[confirmDeleteHistory.id])}
        />
      )}

      {confirmDeletePerson && (
        <ConfirmModal
          tag="⚠ Remove person"
          title={`Remove ${person.name}?`}
          message="All occasions, gift history, and saved ideas for this person will be permanently deleted."
          confirmLabel="Remove person"
          onCancel={() => setConfirmDeletePerson(false)}
          onConfirm={async () => {
            setConfirmDeletePerson(false);
            setDeleteNoticeMessage("Removing person...");
            try {
              await peopleApi.delete(person.id);
              toast?.(`${person.name} removed`, "info");
              navigate("/app/people");
            } finally {
              setDeleteNoticeMessage("");
            }
          }}
          loading={deleteNoticeMessage === "Removing person..."}
        />
      )}

      {/* Saved gift ideas (wishlist) */}
      {savedIdeas.length > 0 && (
        <>
          <div className="flex-between mb-16" style={{ alignItems: "baseline", gap: 12, marginTop: 8 }}>
            <div>
              <h3 style={{ fontSize: 20, marginBottom: 4 }}>🎁 Saved gift ideas</h3>
              <p className="text-sm text-muted" style={{ margin: 0 }}>
                Ideas you've shortlisted — mark purchased when you buy one.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: savedIdeas.length > SAVED_CAP ? 8 : 32 }}>
            {(showAllSaved ? savedIdeas : savedIdeas.slice(0, SAVED_CAP)).map(s => (
              <div key={s.id} className="suggestion-card saved-idea-card">
                <div className="flex-between mb-8" style={{ alignItems: "center", gap: 10 }}>
                  {s.occasion_type ? (
                    <span className="badge" style={{ background: "var(--cream)", color: "var(--ink-soft)", textTransform: "capitalize" }}>
                      {s.occasion_type.replace(/_/g, " ")}
                    </span>
                  ) : <span />}
                  {(s.price_min || s.price_max) && (
                    <span style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--ink-soft)",
                      background: "var(--cream)",
                      padding: "3px 10px",
                      borderRadius: 20,
                      border: "1px solid var(--border)"
                    }}>
                      {s.price_min && s.price_max
                        ? `$${s.price_min}–$${s.price_max}`
                        : s.price_max ? `~$${s.price_max}` : `$${s.price_min}+`}
                    </span>
                  )}
                </div>

                <div className="suggestion-title">{s.title}</div>

                {s.why_it_fits && (
                  <div className="suggestion-why">✦ {s.why_it_fits}</div>
                )}

                {buildRetailerLinks({
                  title: s.title,
                  amazonUrl: s.amazon_url,
                  etsyUrl: s.etsy_url,
                  shoppingRegion,
                  isUnique: isUniqueGiftIdea(s),
                }).length > 0 && (
                  <div className="suggestion-actions" style={{ marginTop: 12 }}>
                    {buildRetailerLinks({
                      title: s.title,
                      amazonUrl: s.amazon_url,
                      etsyUrl: s.etsy_url,
                      shoppingRegion,
                      isUnique: isUniqueGiftIdea(s),
                    }).map(link => (
                      <a
                        key={link.key}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`affiliate-btn ${link.className}`}
                        onClick={() => affiliateApi.track(link.program, "gifts", link.url).catch(() => {})}
                      >
                        {link.label}
                        <span style={{ fontSize: "0.75em", opacity: 0.6, marginLeft: 3 }}>↗</span>
                      </a>
                    ))}
                  </div>
                )}

                <div className="saved-idea-actions">
                  <button
                    className="btn btn-sm"
                    style={{ color: "var(--green)", borderColor: "var(--green)", background: "transparent" }}
                    disabled={savedBusy[s.id]}
                    onClick={() => handleSavedPurchase(s.id)}
                  >
                    Mark purchased
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={savedBusy[s.id]}
                    onClick={() => handleSavedUnsave(s.id)}
                  >
                    Unsave
                  </button>
                </div>
              </div>
            ))}
          </div>
          {savedIdeas.length > SAVED_CAP && (
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowAllSaved(v => !v)}
              >
                {showAllSaved
                  ? "Show less"
                  : `+ ${savedIdeas.length - SAVED_CAP} more idea${savedIdeas.length - SAVED_CAP !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Gift history */}
      {history.length > 0 && (
        <>
          <div id="gift-history" className="flex-between mb-16" style={{ alignItems: "baseline", gap: 12 }}>
            <div>
              <h3 style={{ fontSize: 20, marginBottom: 4 }}>📜 Gift history</h3>
              <p className="text-sm text-muted" style={{ margin: 0 }}>
                Recent years stay visible first. Older gift history is archived below.
              </p>
            </div>
            {olderHistoryYears.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowOlderHistory(v => !v)}>
                {showOlderHistory ? "Hide archive" : `Show older years (${olderHistoryYears.length})`}
              </button>
            )}
          </div>
          <div className="gift-history-list">
            {[...recentHistoryYears, ...(showOlderHistory ? olderHistoryYears : [])].map((year) => {
              const entries = groupedHistory[year] || [];
              const expanded = Boolean(expandedHistoryYears[year]);
              const visibleEntries = expanded ? entries : entries.slice(0, 6);
              return (
              <section key={year} className="gift-history-year">
                <div className="gift-history-year-title">
                  <span>{year}</span>
                  <small>{entries.length} gift{entries.length !== 1 ? "s" : ""}</small>
                </div>
                <div className="gift-history-year-list">
                  {visibleEntries.map(h => {
                    const [hbg, hfg] = REACTION_COLORS[h.recipient_reaction] || ["var(--cream)", "var(--ink-muted)"];
                    return (
                      <div key={h.id} className="suggestion-card saved-idea-card gift-history-card">
                        <div className="gift-history-card-main">
                          <div className="gift-history-card-copy">
                            <div className="gift-history-card-title">{h.gift_name}</div>
                            <div className="text-sm text-muted gift-history-meta">
                              {[h.occasion_type.replace(/_/g, " "), formatGiftHistoryDate(h)].filter(Boolean).join(" · ")}
                            </div>
                            {h.gift_notes && (
                              <div className="text-sm text-muted gift-history-notes">{h.gift_notes}</div>
                            )}
                          </div>
                          <div className="gift-history-side">
                            {h.amount_spent ? <strong className="gift-history-price">${Number(h.amount_spent).toFixed(0)}</strong> : null}
                            {h.recipient_reaction && (
                              <span className="badge" style={{ background: hbg, color: hfg }}>
                                {h.recipient_reaction}
                              </span>
                            )}
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setConfirmDeleteHistory(h)}
                              disabled={Boolean(historyBusy[h.id])}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {entries.length > 6 && (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggleHistoryYear(year)}>
                        {expanded ? "Show less" : `Show ${entries.length - visibleEntries.length} more`}
                      </button>
                    </div>
                  )}
                </div>
              </section>
      )})}
          </div>
        </>
      )}

      <div className="fd-food-askbar gf-askbar gf-detail-askbar">
        <input
          className="fd-food-askbar__input"
          type="text"
          placeholder={`✦ Ask AI about gifts for ${person.name}...`}
          aria-label={`Ask HouseMind AI about gifts for ${person.name}`}
          onFocus={() => openGiftAsk(`${person.name} gift ideas`)}
          readOnly
        />
        <div className="fd-food-askbar__chips">
          {["Gift ideas", "Budget help", "Occasion plan"].map(prompt => (
            <button
              key={prompt}
              type="button"
              className="fd-food-askbar__chip"
              onClick={() => openGiftAsk(`${person.name}: ${prompt}`)}
            >
              {prompt}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="fd-food-askbar__button"
          onClick={() => openGiftAsk(`${person.name} gift ideas`)}
        >
          ✦ Ask
        </button>
      </div>

      {showDeleteNotice && <SavingNotice title="Removing" message={deleteNoticeMessage || "Removing occasion..."} />}
      {showSavingNotice && <SavingNotice message="Saving occasion..." />}
    </div>
  );
}
